<script lang="ts">
  import { tick, getContext } from 'svelte';
  import Cookies from 'js-cookie';
  import { Text, SubmitButton } from '@squareup/public-web-design-system';
  import { type ColorMode } from '@squareup/public-web-design-system';
  import { filterCDPProperties } from '@squareup/public-web-browser-helpers/cdp-formatter';
  import type { FormField } from './types';

  import VisibleField from './fields/VisibleField.svelte';
  import FollowUpListHiddenField from './fields/FollowUpListHiddenField.svelte';
  import HiddenField from './fields/HiddenField.svelte';
  import MunchkinTrackHiddenField from './fields/MunchkinTrackHiddenField.svelte';
  import RichTextField from './fields/RichTextField.svelte';
  import FlashBannerPartial from '$components/common/partials/FlashBannerPartial/FlashBannerPartial.svelte';
  import driftHelper from '$lib/helpers/browser/drift-helper';
  import { hashString } from '$lib/helpers/browser/utils';
  import { SAASQUATCH_COOKIE_NAME, RSCODE_COOKIE_NAME, TRACK_COOKIE_NAME } from '$lib/constants/cookie-constants';
  import { FORM_SUBMIT_SUCCESS } from '$lib/constants/custom-events';
  import { DesignSystem as DesignSystemEnum } from '$lib/helpers/design-system';

  export let buttonLabel: string | undefined;
  export let followUpList: object | undefined;
  export let visibleRows: FormField[][] | undefined;
  export let hiddenFields: FormField[] | undefined;
  export let legalField: FormField | undefined;
  export let richTextAlignment: 'center' | 'left' | 'right' | 'justify';
  export let formId: string;
  export let submitUrl: string;
  export let locale: string;

  // Translated text
  export let localizedCountryNames: Record<string, string>;
  export let successfulSubmitBannerMessage: string;
  export let successfulSubmitBannerDuration: number | undefined;
  export let successfulSubmitButtonText: string | undefined;
  export let disableBanner: boolean | undefined;
  export let failedSubmitBannerMessage: string | undefined;
  export let areMarketStylesEnabled: boolean | undefined;

  // PWDS specific props
  export let colorMode: ColorMode;

  let hasDoneInitialSubmit: boolean = false;
  let formDisabled: boolean = false;
  let submitDisabled: boolean = false;
  let bannerWasShown: boolean = false;
  let followUpUrl: string;

  let leadFormElement: HTMLFormElement;
  let flashBanner: FlashBannerPartial;
  let singleField: any;
  const visibleFieldsList: VisibleField[] = [];

  const singleFieldForm: boolean = Boolean(visibleRows && visibleRows.length === 1 && visibleRows[0].length === 1);
  const isPWDSEnabled = getContext<DesignSystemEnum>('designSystem') === DesignSystemEnum.PublicWebDesignSystem;

  if (singleFieldForm && visibleRows) {
    singleField = visibleRows[0][0];
  }

  export let showFollowUpForm: (email: string) => void;
  export let hasFollowUpForm: boolean = false;

  async function onSubmit() {
    hasDoneInitialSubmit = true;
    if (!validateAllFields()) {
      await submitFormToMarketo();
      hasDoneInitialSubmit = false;
    }
  }

  function validateAllFields(): string | null {
    // Prevent child fields calling this until we've tried to submit unsuccessfully
    if (hasDoneInitialSubmit) {
      let firstFoundErrorMessage;

      for (const visibleField of visibleFieldsList) {
        // We set the flash banner to the first error message,
        // but don't terminate the loop so we can tooltip any other
        // fields with errors
        const fieldError = visibleField.checkForError();
        if (fieldError && !firstFoundErrorMessage) {
          firstFoundErrorMessage = fieldError;
        }
      }
      if (firstFoundErrorMessage) {
        flashBanner?.showError(firstFoundErrorMessage);
      } else {
        flashBanner?.hideFlashBanner();
      }

      // disable submit until all fields are valid
      submitDisabled = Boolean(firstFoundErrorMessage);

      return firstFoundErrorMessage as string;
    }

    return null;
  }

  async function submitFormToMarketo() {
    formDisabled = true;

    const formData = new FormData(leadFormElement);

    // If multiple fields with same name (checkboxes), combine into CSV string
    const formFieldNames = Array.from(formData.keys());
    formFieldNames.forEach((fieldName) => {
      const fieldValues = formData.getAll(fieldName);
      if (fieldValues.length > 1) {
        formData.set(fieldName, fieldValues.join(', '));
      }
    });

    const paramsAsObject = Object.fromEntries(formData.entries());
    const payload: { [key: string]: any } = {
      formId,
      leadFormFields: paramsAsObject,
    };

    // Send user info to Drift so we can link this form submission to their Drift instance
    // No-op if Drift is not loaded
    driftHelper.setContactInfo({
      email: payload.leadFormFields.Email,
      employmentName: payload.leadFormFields.Company,
      'First Name': payload.leadFormFields.FirstName,
      'Last Name': payload.leadFormFields.LastName,
      Phone: payload.leadFormFields.Phone,
      'Revenue Range': payload.leadFormFields.Revenue_Range__c_account,
    });

    if (hasFollowUpForm) {
      // We can safely assume every form has an Email field, because Marketo uses it for UUID
      showFollowUpForm(payload.leadFormFields.Email);
    }

    try {
      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        bannerWasShown = false;
        await trackSuccess(payload);

        if (!hasFollowUpForm) {
          // Set followUpUrl in component state so any showSuccessOrRedirect
          // call can have it without excessive variable passing
          followUpUrl = data.followUpUrl;
          showSuccessOrRedirect();
        }
      }

      // We reached Marketo, but what we submitted was rejected
      else {
        flashBanner?.showError(failedSubmitBannerMessage as string);
        trackFailure(payload);
        formDisabled = false;
      }

      // We did not reach Marketo at all
    } catch {
      flashBanner?.showError(failedSubmitBannerMessage as string);
      trackFailure(payload);
      formDisabled = false;
    }
  }

  function showSuccessOrRedirect() {
    leadFormElement.dispatchEvent(new Event(FORM_SUBMIT_SUCCESS, { bubbles: true }));
    // Don't show a banner or redirect if we have a follow up form
    if (hasFollowUpForm) {
      return;
    }
    // If we have a follow-up URL, redirect there
    if (followUpUrl) {
      window.location.href = followUpUrl;
      return;
    }
    // Otherwise show a success banner, presuming we haven't already
    if (!bannerWasShown) {
      flashBanner?.showSuccess(successfulSubmitBannerMessage, successfulSubmitBannerDuration as number);
      const originalButtonLabel = buttonLabel;
      buttonLabel = successfulSubmitButtonText as string;
      bannerWasShown = true;

      setTimeout(async () => {
        formDisabled = false;
        buttonLabel = originalButtonLabel;
        leadFormElement.reset();

        // Must wait tick to fully reset Svelte variables bound to inputs
        await tick();

        // Reset the Svelte-bound values for PWDS fields
        visibleFieldsList.forEach((field) => {
          field.clearValue();
        });
      }, successfulSubmitBannerDuration);
    }
  }

  // All JS below this point is related to tracking

  async function trackSuccess(payload: Record<string, Record<string, string>>) {
    // What we track is not directly equal to what the form
    // submits to marketo, so we have to do some parsing.
    const eventData = buildTrackingEventData(payload);
    const hashedEmail = await hashString(payload.leadFormFields.Email);
    const hashedEmailSha1 = await hashString(payload.leadFormFields.Email, 'SHA-1');

    const properties = {
      formDetail: eventData.formDetail,
      formID: eventData.formId,
      formType: eventData.FORM_TYPE,
      industry: eventData.industry,
      isCurrentSeller: eventData.isCurrentSeller,
      leadCountry: eventData.leadCountry,
      leadSource: eventData.LEAD_SOURCE,
      leadState: eventData.leadState,
      revenueRange: eventData.revenueRange,
    };
    const filteredProps = filterCDPProperties(properties);
    window.Sq.Tracking.track(
      {
        action: 'Submit Lead Form',
        properties: filteredProps,
      },
      { destinations: ['cdp'] }
    );

    const rsCodeValue = Cookies.get(RSCODE_COOKIE_NAME);
    const saasquatchValue = Cookies.get(SAASQUATCH_COOKIE_NAME);
    const trackCookieBase64Value = Cookies.get(TRACK_COOKIE_NAME);
    const martechEvent = {
      form_submission: true,
      industry: eventData.industry,
      estimated_annual_revenue: eventData.revenueRange,
      email: hashedEmail,
      email_sha1: hashedEmailSha1,
      trackCookieBase64: trackCookieBase64Value,
      ...(rsCodeValue !== undefined && { [RSCODE_COOKIE_NAME]: rsCodeValue }),
      ...(saasquatchValue !== undefined && { [SAASQUATCH_COOKIE_NAME]: saasquatchValue }),
    };

    window.Sq.Tracking.track(
      {
        action: 'Submit Lead Form',
        properties: martechEvent,
      },
      { destinations: ['martechJs'] }
    );

    const gtmEvent = {
      form_submission: true,
      industry: eventData.industry,
      estimated_annual_revenue: eventData.revenueRange,
      email_sha1: hashedEmailSha1,
    };
    window.Sq.Tracking.track(
      {
        action: 'Submit Lead Form',
        properties: gtmEvent,
      },
      { destinations: ['googleTagManager'] }
    );
    // In edge cases where things hang because GTM is adblocked, we'll just ensure we return after 3 seconds
    // (Same as onboard - https://github.com/squareup/onboard-frontend/blob/621010d2e507d5b3a27b58d0df83e82968109e88/app/utils/track-tealium-and-martech-event.js#L7)
    setTimeout(() => showSuccessOrRedirect(), 3000);
  }

  function trackFailure(payload: Record<string, Record<string, string>>) {
    const eventData = buildTrackingEventData(payload);
    const properties = {
      formDetail: eventData.formDetail,
      formID: eventData.formId,
      formType: eventData.FORM_TYPE,
      industry: eventData.industry,
      isCurrentSeller: eventData.isCurrentSeller,
      leadCountry: eventData.leadCountry,
      leadSource: eventData.LEAD_SOURCE,
      leadState: eventData.leadState,
      revenueRange: eventData.revenueRange,
    };
    const filteredProps = filterCDPProperties(properties);
    window.Sq.Tracking.track(
      {
        action: 'Submit Lead Form Failure',
        properties: filteredProps,
      },
      { destinations: ['cdp'] }
    );
  }

  function buildTrackingEventData(payload: Record<string, Record<string, string>>) {
    let defaultEventData: Record<string, string>;

    switch (payload.leadFormFields.formType) {
      case 'Partnerships': {
        defaultEventData = {
          EVENT_NAME: 'Partner',
          EVENT_VALUE: 'Create',
          LEAD_TYPE: 'partnership',
          LEAD_SOURCE: 'Partner',
          SOURCE_DETAIL: 'Authorized',
          FORM_TYPE: 'Partnerships',
        };
        break;
      }
      case 'Events': {
        defaultEventData = {
          EVENT_NAME: 'Event',
          EVENT_VALUE: 'Create',
          LEAD_TYPE: 'event',
          LEAD_SOURCE: 'Event',
          FORM_TYPE: 'Events',
        };
        break;
      }
      default: {
        defaultEventData = {
          EVENT_NAME: 'Lead Form',
          EVENT_VALUE: 'Create',
          LEAD_TYPE: 'square',
          FORM_TYPE: 'Leads',
        };
      }
    }

    // The key names here are set in Marketo, the lead gen team keeps them consistent
    const eventDataFromForm: Record<string, string | Record<string, string>> = {
      formId: payload.formId.toString(),
      externalSource: payload.leadFormFields.External_Source__c,
      formDetail: payload.leadFormFields.formDetail,
      industry: payload.leadFormFields.Industry,
      isCurrentSeller: payload.leadFormFields.currentSquareseller,
      leadCountry: payload.leadFormFields.Country,
      leadState: payload.leadFormFields.State,
      revenueRange: payload.leadFormFields.Revenue_Range__c_account,
      sourceDetail: payload.leadFormFields.Source_Detail__c_account,
      avt: payload.leadFormFields.Square_AVT__c,
    };

    return { ...defaultEventData, ...eventDataFromForm };
  }

  // This function removes the top level <p> tag from the legal text.
  // When legal text is rendered inside PWDS's Text the outer <p> tag is
  // not needed. The Text component already wraps the text in a <p> tag.
  // Having double <p> tags is invalid and leads to CLS issues.
  function extractLegalTextFromOuterParagraph(input: string) {
    const regex = /^<p>(.+)<\/p>$/;
    const match = input.match(regex);

    return match ? match[1] : input;
  }
</script>

<div class="formContainer">
  <form novalidate bind:this={leadFormElement} on:submit|preventDefault={onSubmit}>
    <fieldset disabled={isPWDSEnabled ? undefined : formDisabled}>
      {#if singleFieldForm}
        <div class="singleFieldForm" class:singleFieldForm--is-pwds={isPWDSEnabled}>
          <VisibleField
            bind:this={visibleFieldsList[visibleFieldsList.length]}
            validateAllParentFields={validateAllFields}
            field={singleField}
            {localizedCountryNames}
            {areMarketStylesEnabled}
            {locale}
            {colorMode}
            disabled={formDisabled}
          />
          {#if isPWDSEnabled}
            <SubmitButton {colorMode} disabled={formDisabled || submitDisabled} text={buttonLabel} />
          {:else}
            <div class="singleFieldFormButton">
              <button class="submitButton" type="submit" disabled={formDisabled || submitDisabled}>
                {buttonLabel}
              </button>
            </div>
          {/if}
        </div>
      {:else if visibleRows}
        {#each visibleRows as row}
          <div class="row row-with-{row.length}-columns">
            {#each row as field}
              <VisibleField
                bind:this={visibleFieldsList[visibleFieldsList.length]}
                validateAllParentFields={validateAllFields}
                {field}
                {localizedCountryNames}
                {areMarketStylesEnabled}
                {locale}
                {colorMode}
                disabled={formDisabled}
              />
            {/each}
          </div>
        {/each}
      {/if}
    </fieldset>

    {#if hiddenFields}
      {#each hiddenFields as field}
        <HiddenField {field} />
      {/each}
    {/if}

    <MunchkinTrackHiddenField />
    {#if followUpList}
      <FollowUpListHiddenField {followUpList} />
    {/if}

    {#if !singleFieldForm}
      {#if isPWDSEnabled}
        <SubmitButton {colorMode} disabled={formDisabled || submitDisabled} text={buttonLabel} />
      {:else}
        <button class="submitButton" type="submit" disabled={formDisabled || submitDisabled}>{buttonLabel}</button>
      {/if}
    {/if}

    {#if legalField?.text}
      {#if isPWDSEnabled}
        <Text class="legalField" variant="50-r" data-color-mode={colorMode}>
          {@html extractLegalTextFromOuterParagraph(legalField.text)}
        </Text>
      {:else}
        <div class="rtfContainer" style:text-align={richTextAlignment} class:marketField={areMarketStylesEnabled}>
          <RichTextField text={legalField.text} isLegalField={true} />
        </div>
      {/if}
    {/if}
  </form>
</div>

{#if !disableBanner}
  <FlashBannerPartial bind:this={flashBanner} />
{/if}

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/media_queries' as media;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/mixins/ui.scss' as ui;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/mixins/grid.scss' as grid;

  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/media_queries' as pwdsMediaQueries;
  @use '@squareup/public-web-design-system/dist/styles/mixins/layout' as pwdsLayout;

  .formContainer {
    @include grid.flex($at: 'medium');
    @include grid.space(10);
    @include grid.valign('bottom');
  }

  form {
    width: 100% !important;
  }

  fieldset:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  .row {
    display: flex;
    margin: 0 calc(-1 * var(--space-10)) 0 calc(-1 * var(--space-10));

    @include media.until('medium') {
      display: block;
    }
  }

  .singleFieldForm {
    @include grid.auto;
    @include grid.flex($at: 'medium');
    @include grid.space(10);
    @include grid.valign('bottom');

    &.singleFieldForm--is-pwds {
      @include grid.flex($at: 'small');

      @include grid.space(20);
      // It is aligned to the top because PWDS has a different error state that adds to the height of the container
      @include grid.valign('top');

      @include pwdsMediaQueries.at('medium') {
        width: pwdsLayout.width-of-columns(8);
      }

      @include pwdsMediaQueries.at('large') {
        width: pwdsLayout.width-of-columns(6);
      }
    }

    .singleFieldFormButton {
      flex-shrink: 0 !important;
      width: auto;

      @include media.at('medium') {
        padding-right: 0;
      }

      @include media.until('small') {
        padding-left: 0;
        padding-right: 0;
      }

      @include media.until('medium') {
        width: 100%;
      }
    }

    :global {
      .VisibleField,
      .PwdsVisibleField {
        flex: 0 1 auto;
        min-width: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        padding: var(--space-none) !important;
      }

      .VisibleField {
        @include media.until('medium') {
          margin-bottom: var(--space-none) !important;
        }
      }

      .PwdsVisibleField {
        margin-bottom: var(--space-none);
      }
    }
  }

  .submitButton {
    @include ui.button;
    width: 100%;
    margin-bottom: var(--space-20);
    padding: 1rem 2rem;
  }

  .rtfContainer {
    margin-bottom: var(--space-10);
  }

  :global(.LeadFormSection:not(.LeadFormSection--is-pwds)) {
    a {
      font-weight: var(--font-weight-medium);
      color: var(--color-blue);
      text-decoration: none;
    }
  }

  :global(.LeadFormSection) {
    .marketField {
      a {
        color: var(--color-gray-99);
        text-decoration: underline;

        &:hover {
          color: var(--color-gray-80);
        }
      }
    }
  }

  @for $i from 1 through 5 {
    .row-with-#{$i}-columns {
      $col-width: calc(100% / $i);

      > * {
        width: $col-width;
      }
    }
  }

  :global {
    .legalField {
      // Need higher specificity because .Text has margin: 0; styling applied
      &.Text {
        margin-top: var(--space-20) !important;

        &,
        &[data-color-mode='light'] {
          --color: var(--color-neutral-400);
        }

        &[data-color-mode='dark'] {
          --color: var(--color-neutral-600);
        }
      }

      a {
        font-weight: var(--font-weight-regular);
        text-decoration: underline;

        &,
        &:hover {
          color: var(--color);
        }
      }
    }

    .LeadFormSection--is-pwds {
      .row-with-1-columns {
        > * {
          @include pwdsMediaQueries.at('medium') {
            width: pwdsLayout.width-of-columns(8);
          }

          @include pwdsMediaQueries.at('large') {
            width: pwdsLayout.width-of-columns(6);
          }
        }
      }
    }
  }
</style>
